import { invoke } from '@tauri-apps/api/core'
import { ref, watch, type WatchStopHandle } from 'vue'

import { connectivityService } from '@/services/connectivityService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauri } from '@/services/platformService'

const networkAllowed = ref(false)
const policyInitialized = ref(false)
const policySynchronized = ref(false)

let desiredNetworkAllowed = false
let synchronization: Promise<void> | null = null
let stopConnectivityWatch: WatchStopHandle | null = null
let retryTimer: number | null = null
let retryAttempt = 0

const INITIAL_RETRY_DELAY_MS = 250
const MAX_RETRY_DELAY_MS = 30_000

function clearRetryTimer(): void {
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer)
    retryTimer = null
  }
}

function scheduleRetry(): void {
  if (retryTimer !== null || synchronization || policySynchronized.value) return

  const delay = Math.min(INITIAL_RETRY_DELAY_MS * 2 ** retryAttempt, MAX_RETRY_DELAY_MS)
  retryAttempt += 1
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    void synchronizeNetworkPolicy()
  }, delay)
}

function synchronizeNetworkPolicy(): Promise<void> {
  if (synchronization) return synchronization

  clearRetryTimer()
  policySynchronized.value = false
  const currentSynchronization = (async () => {
    while (!policyInitialized.value || networkAllowed.value !== desiredNetworkAllowed) {
      const nextNetworkAllowed = desiredNetworkAllowed
      await invoke('set_tile_proxy_network_allowed', { networkAllowed: nextNetworkAllowed })
      networkAllowed.value = nextNetworkAllowed
      policyInitialized.value = true
    }
  })()

  synchronization = currentSynchronization.then(
    async () => {
      synchronization = null
      if (!policyInitialized.value || networkAllowed.value !== desiredNetworkAllowed) {
        await synchronizeNetworkPolicy()
        return
      }
      retryAttempt = 0
      policySynchronized.value = true
    },
    (error: unknown) => {
      synchronization = null
      logSynchronizationFailure(error)
      scheduleRetry()
    },
  )

  return synchronization
}

function logSynchronizationFailure(error: unknown): void {
  logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to sync tile proxy network policy to Tauri', {
    error,
  })
}

/**
 * Mirrors effective connectivity into the native tile proxy before the app mounts.
 * Native startup is cache-only, so a failed initial synchronization fails closed.
 */
async function initialize(): Promise<void> {
  if (!isTauri()) {
    policyInitialized.value = true
    policySynchronized.value = true
    return
  }
  if (stopConnectivityWatch) return

  desiredNetworkAllowed = connectivityService.isEffectivelyOnline.value
  stopConnectivityWatch = watch(connectivityService.isEffectivelyOnline, (online) => {
    desiredNetworkAllowed = online
    policySynchronized.value = false
    void synchronizeNetworkPolicy()
  })

  // Rust starts cache-only, so startup can safely continue if IPC is temporarily unavailable. The
  // reconciler keeps retrying in the background while MapLibre waits for policySynchronized.
  await synchronizeNetworkPolicy()
}

export const nativeTileProxyService = {
  networkAllowed,
  policyInitialized,
  policySynchronized,
  initialize,
}
